"use client"

import { useState } from "react"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts"
import { Card } from "@/components/ui/card"
import { TrendingUp, ChevronRight, Target } from "lucide-react"

interface CareerPathChartProps {
  data: Array<{
    name: string
    value: number
    color: string
  }>
  title?: string
  description?: string
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-gray-200 rounded-lg shadow-lg p-4">
        <div className="flex items-center gap-2 mb-2">
          <div 
            className="w-3 h-3 rounded-full" 
            style={{ backgroundColor: payload[0].payload.color }}
          />
          <p className="text-sm font-semibold text-gray-900">{label}</p>
        </div>
        <p className="text-lg font-bold text-gray-900">{payload[0].value.toLocaleString()}</p>
        <p className="text-xs text-gray-500 mt-1">students pursuing this path</p>
      </div>
    )
  }
  return null
}

export function CareerPathChart({ data, title = "Career Path Distribution", description = "Current distribution of students across different career paths" }: CareerPathChartProps) {
  const [activeIndex, setActiveIndex] = useState<number | null>(null)
  const total = data.reduce((sum, item) => sum + item.value, 0)

  return (
    <Card className="p-6 border border-gray-200 bg-gradient-to-br from-white to-gray-50/50 shadow-lg hover:shadow-xl transition-all duration-300">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          </div>
          <p className="text-gray-600 text-sm">{description}</p>
        </div>
        
        {/* Stats Summary */}
        <div className="flex items-center gap-4 px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-100">
          <Target className="w-4 h-4 text-blue-600" />
          <div>
            <p className="text-xs text-gray-500">Total Students</p>
            <p className="text-lg font-bold text-gray-900">{total.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Chart Container */}
      <div className="relative">
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 30, left: 0, bottom: 10 }}
            onMouseMove={(state) => {
              if (state.isTooltipActive) {
                setActiveIndex(state.activeTooltipIndex || null)
              } else {
                setActiveIndex(null)
              }
            }}
            onMouseLeave={() => setActiveIndex(null)}
          >
            <defs>
              {data.map((entry, index) => (
                <linearGradient
                  key={`gradient-${index}`}
                  id={`colorGradient${index}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop 
                    offset="0%" 
                    stopColor={entry.color} 
                    stopOpacity={activeIndex === index ? 1 : 0.9}
                  />
                  <stop 
                    offset="100%" 
                    stopColor={entry.color} 
                    stopOpacity={activeIndex === index ? 0.8 : 0.6}
                  />
                </linearGradient>
              ))}
            </defs>
            
            <CartesianGrid 
              strokeDasharray="3 3" 
              stroke="#e5e7eb" 
              vertical={false}
              strokeOpacity={0.3}
            />
            
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
              padding={{ left: 20, right: 20 }}
            />
            
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#6b7280', fontSize: 12 }}
              tickFormatter={(value) => value.toLocaleString()}
            />
            
            <Tooltip 
              content={<CustomTooltip />}
              cursor={{ fill: 'transparent' }}
            />
            
            <Bar
              dataKey="value"
              radius={[12, 12, 0, 0]}
              animationDuration={1500}
              animationBegin={200}
            >
              {data.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={`url(#colorGradient${index})`}
                  stroke={activeIndex === index ? entry.color : 'transparent'}
                  strokeWidth={activeIndex === index ? 2 : 0}
                  className="transition-all duration-300 hover:opacity-90 cursor-pointer"
                  onMouseEnter={() => setActiveIndex(index)}
                  onMouseLeave={() => setActiveIndex(null)}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div className="mt-8 pt-6 border-t border-gray-100">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {data.map((item, index) => (
              <div
                key={index}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${
                  activeIndex === index ? 'bg-gray-100' : ''
                }`}
                onMouseEnter={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(null)}
              >
                <div 
                  className="w-3 h-3 rounded-full transition-transform duration-200"
                  style={{ 
                    backgroundColor: item.color,
                    transform: activeIndex === index ? 'scale(1.2)' : 'scale(1)'
                  }}
                />
                <span className="text-sm font-medium text-gray-700">{item.name}</span>
                <span className="text-sm font-bold text-gray-900">
                  {((item.value / total) * 100).toFixed(1)}%
                </span>
              </div>
            ))}
          </div>
          
          <button className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors group">
            View Details
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      {/* Insights */}
      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50/50 to-purple-50/50 rounded-lg border border-blue-100">
        <p className="text-sm text-gray-700 flex items-center gap-2">
          <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
          <span className="font-medium">Insight:</span> 
          <span className="text-gray-600">
            {data[0]?.name} is the most popular career path with {((data[0]?.value / total) * 100).toFixed(0)}% of students
          </span>
        </p>
      </div>
    </Card>
  )
}